import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from 'antd'
import { INLINE_NODE, ORDER_ACTIONS, actionMatchesRoles } from '@/lib/order-actions'

interface NodeContext {
  roles: string[]
  targets: Record<string, HTMLDivElement | null>
  register: (key: string, element: HTMLDivElement | null) => void
}
const Context = createContext<NodeContext | null>(null)

/**
 * Keep existing form state/commands while presenting each action at its placement:
 * workflow actions under their progress node, other order actions in the fixed bottom bar,
 * and per-record links (certificate/invoice download) inline beside the record.
 */
export function OrderNodeProvider({ roles, children }: { roles: string[]; children: ReactNode }) {
  const [targets, setTargets] = useState<Record<string, HTMLDivElement | null>>({})
  const register = useCallback((key: string, element: HTMLDivElement | null) => {
    setTargets(current => current[key] === element ? current : { ...current, [key]: element })
  }, [])
  return <Context.Provider value={{ roles, targets, register }}>{children}</Context.Provider>
}

export function OrderNodeOutlet({ node, className }: { node: string; className?: string }) {
  const register = useContext(Context)?.register
  const ref = useCallback((element: HTMLDivElement | null) => register?.(node, element), [register, node])
  return <div ref={ref} data-node-actions={node} className={className ?? 'order-node-actions mt-2 flex flex-wrap items-center gap-2 empty:hidden'} />
}

export function OrderNodeAction({ action, children, description, subject, node, tooltipHidden }: {
  action: string; children: ReactNode; description?: string; subject?: string; node?: string
  /** Hide the hover explanation while this action's own menu/popover is open. */
  tooltipHidden?: boolean
}) {
  const context = useContext(Context)
  const meta = ORDER_ACTIONS[action]
  if (!meta || (context && !actionMatchesRoles(action, context.roles))) return null
  const placement = node || meta.node
  const content = <div data-order-action={action} className="max-w-full">
    {subject && <div className="mb-1 text-xs text-slate-500 break-all">{subject}</div>}
    <Tooltip title={description || meta.description} trigger={['hover', 'focus']} {...(tooltipHidden ? { open: false } : {})}>
      <span tabIndex={0} className="inline-flex max-w-full" aria-label={`${subject ? subject + '：' : ''}${description || meta.description}`}>{children}</span>
    </Tooltip>
  </div>
  if (!context || placement === INLINE_NODE) return content
  const target = context.targets[placement]
  return target ? createPortal(content, target) : null
}
